import { Box, Grid, Typography } from '@mui/material';
import { ReactText, useState } from 'react';
import { styled } from '@mui/system';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
  CustomButton,
  CustomTextField,
  CustomTextFieldAmount,
} from 'components/common';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MarketBox } from './MarketBox';
import { MaxLengthText } from './MaxLengthText';
import { ProjectionsTable } from './ProjectionsTable';
import { TeamMember } from './TeamMember';
import { toast } from 'react-toastify';
import siteLogo from 'assets/images/logo.svg';
import OnePagerPreviewModal from './OnePagerPreviewModal';
import OnePagerCommonModal from './OnePagerCommonModal';
import { uploadFile } from 'utils/awsUpload';
import { ApiPost } from 'helpers/Api/ApiData';

interface CurrencyButtonProps {
  selected: boolean;
  children: ReactText;
}

const CurrencyButton = styled('span')<CurrencyButtonProps>(({ selected }) => ({
  backgroundColor: selected ? '#008B5E' : '#F7FFFC',
  padding: '5px 18px',
  fontSize: '26px',
  fontWeight: 400,
  cursor: 'pointer',
  color: selected ? '#fff' : '#008B5E',
}));

export interface OnePagerFormFields {
  company_description: string;
  problem: string;
  solution: string;
  totalMarket: string;
  serviceMarket: string;
  serviceObtMarket: string;
  landscape: string;
  revenue: string;
  strategy: string;
  traction: string;
  yearOne: string;
  yearTwo: string;
  yearThree: string;
  yearFour: string;
  yearFive: string;
  revenueAmountOne: string;
  revenueAmountTwo: string;
  revenueAmountThree: string;
  revenueAmountFour: string;
  revenueAmountFive: string;
  expensesAmountOne: string;
  expensesAmountTwo: string;
  expensesAmountThree: string;
  expensesAmountFour: string;
  expensesAmountFive: string;
  surnameOne: string;
  surnameTwo: string;
  surnameThree: string;
  surnameFour: string;
  DesignationOne: string;
  DesignationTwo: string;
  DesignationThree: string;
  DesignationFour: string;
  fundAmount: string;
  companyLogo: File;
  fundAskingImg: File;
  teamImageOne: File[];
  teamImageTwo: File[];
  teamImageThree: File[];
  teamImageFour: File[];
  fullName: string;
  mobileNumber: string;
  email: string;
}

const yearValidation = yup
  .number()
  .typeError('Invalid year')
  .nullable()
  .required('Year is Required')
  .min(1990, 'Invalid year')
  .max(2030, 'Invalid year');

const amountValidation = (name = 'Amount') =>
  yup
    .string()
    .required(`${name} is required`)
    .matches(/^[-+]?(?:[0-9]+,)*[0-9]+(?:\.[0-9]+)?$/gm, {
      message: 'Incorrect amount',
      excludeEmptyString: true,
    });

type FileName =
  | 'companyLogo'
  | 'fundAskingImg'
  | 'teamImageOne'
  | 'teamImageTwo'
  | 'teamImageThree'
  | 'teamImageFour';

export const OnePagerForm = () => {
  const [currency, setCurrency] = useState<'inr' | 'usd'>('inr');
  // const theme = useTheme();
  const [onePagerData, setOnePagerData] = useState({});
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openEnterEmailModal, setOpenEnterEmailModal] = useState(false);
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false);
  const [submitEmail, setSubmitEmail] = useState<{ email: string }>();
  const [loader, setLoader] = useState(false);
  const [openModalOfOnePager, setOpenModalOfOnePager] = useState(true);
  const [imageUrls, setImageUrls] = useState({});
  const [imageData, setImageData] = useState({});

  // const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [allFilesImages, setAllFilesImages] = useState<
    Partial<Record<FileName, File>>
  >({});

  const schema = yup.object().shape(
    {
      companyLogo: yup
        .mixed()
        .test(
          'companyLogo_required',
          'Company logo is required',
          (value: File[]) => {
            if (allFilesImages.companyLogo) return true;
            return !!(value && value.length);
          },
        ),
      fundAskingImg: yup
        .mixed()
        .test(
          'fundAskingImg',
          'Funding utilization image is required',
          (value: File[]) => {
            if (allFilesImages.fundAskingImg) return true;
            return !!(value && value.length);
          },
        ),
      company_description: yup
        .string()
        .required('Company description is required')
        .max(120),
      totalMarket: amountValidation('Total addressable market'),
      serviceMarket: amountValidation('Service addressable market'),
      serviceObtMarket: amountValidation('Service obtainable market'),
      problem: yup.string().required('Problem is required').max(120),
      solution: yup.string().required('Solution is required').max(120),
      landscape: yup
        .string()
        .required('Competitive landscape is required')
        .max(120),
      revenue: yup.string().required('Revenue model is required').max(120),
      strategy: yup.string().required('Growth strategy is required').max(150),
      traction: yup.string().required('Market traction is required').max(200),
      yearOne: yearValidation,
      yearTwo: yearValidation,
      yearThree: yearValidation,
      yearFour: yearValidation,
      yearFive: yearValidation,
      revenueAmountOne: amountValidation(),
      revenueAmountTwo: amountValidation(),
      revenueAmountThree: amountValidation(),
      revenueAmountFour: amountValidation(),
      revenueAmountFive: amountValidation(),
      expensesAmountOne: amountValidation(),
      expensesAmountTwo: amountValidation(),
      expensesAmountThree: amountValidation(),
      expensesAmountFour: amountValidation(),
      expensesAmountFive: amountValidation(),
      surnameOne: yup
        .string()
        .max(120)
        .when(['DesignationOne', 'teamImageOne'], {
          is: (DesignationOne: string, teamImageOne: string | File[]) =>
            DesignationOne || (teamImageOne && teamImageOne.length),
          then: yup.string().required('Name is required'),
        }),
      surnameTwo: yup
        .string()
        .max(120)
        .when(['DesignationTwo', 'teamImageTwo'], {
          is: (DesignationTwo: string, teamImageTwo: string | File[]) =>
            DesignationTwo || (teamImageTwo && teamImageTwo.length),
          then: yup.string().required('Name is required'),
        }),
      surnameThree: yup
        .string()
        .max(120)
        .when(['DesignationThree', 'teamImageThree'], {
          is: (DesignationThree: string, teamImageThree: string | File[]) =>
            DesignationThree || (teamImageThree && teamImageThree.length),
          then: yup.string().required('Name is required'),
        }),
      surnameFour: yup
        .string()
        .max(120)
        .when(['DesignationFour', 'teamImageFour'], {
          is: (DesignationFour: string, teamImageFour: string | File[]) =>
            DesignationFour || (teamImageFour && teamImageFour.length),
          then: yup.string().required('Name is required'),
        }),
      DesignationOne: yup
        .string()
        .max(120)
        .when(['surnameOne', 'teamImageOne'], {
          is: (surnameOne: string, teamImageOne: string | File[]) =>
            surnameOne || (teamImageOne && teamImageOne.length),
          then: yup.string().required('Designation is required'),
        }),
      DesignationTwo: yup
        .string()
        .max(120)
        .when(['surnameTwo', 'teamImageTwo'], {
          is: (surnameTwo: string, teamImageTwo: string | File[]) =>
            surnameTwo || (teamImageTwo && teamImageTwo.length),
          then: yup.string().required('Designation is required'),
        }),
      DesignationThree: yup
        .string()
        .max(120)
        .when(['surnameThree', 'teamImageThree'], {
          is: (surnameThree: string, teamImageThree: string | File[]) =>
            surnameThree || (teamImageThree && teamImageThree.length),
          then: yup.string().required('Designation is required'),
        }),
      DesignationFour: yup
        .string()
        .max(120)
        .when(['surnameFour', 'teamImageFour'], {
          is: (surnameFour: string, teamImageFour: string | File[]) =>
            surnameFour || (teamImageFour && teamImageFour.length),
          then: yup.string().required('Designation is required'),
        }),
      teamImageOne: yup.mixed().when(['surnameOne', 'DesignationOne'], {
        is: (surnameOne: string, DesignationOne: string) =>
          surnameOne || DesignationOne,
        then: yup
          .mixed()
          .test(
            'teamImageOne_required',
            'Team image is required',
            (value: File[]) => {
              if (allFilesImages.teamImageOne) return true;
              return !!(value && value.length);
            },
          ),
      }),
      teamImageTwo: yup.mixed().when(['surnameTwo', 'DesignationTwo'], {
        is: (surnameTwo: string, DesignationTwo: string) =>
          surnameTwo || DesignationTwo,
        then: yup
          .mixed()
          .test(
            'teamImageTwo_required',
            'Team image is required',
            (value: File[]) => {
              if (allFilesImages.teamImageTwo) return true;
              return !!(value && value.length);
            },
          ),
      }),
      teamImageThree: yup.mixed().when(['surnameThree', 'DesignationThree'], {
        is: (surnameThree: string, DesignationThree: string) =>
          surnameThree || DesignationThree,
        then: yup
          .mixed()
          .test(
            'teamImageThree_required',
            'Team image is required',
            (value: File[]) => {
              if (allFilesImages.teamImageThree) return true;
              return !!(value && value.length);
            },
          ),
      }),
      teamImageFour: yup.mixed().when(['surnameFour', 'DesignationFour'], {
        is: (surnameFour: string, DesignationFour: string) =>
          surnameFour || DesignationFour,
        then: yup
          .mixed()
          .test(
            'teamImageFour_required',
            'Team image is required',
            (value: File[]) => {
              if (allFilesImages.teamImageFour) return true;
              return !!(value && value.length);
            },
          ),
      }),
      fundAmount: amountValidation('Funding Amount'),
      fullName: yup.string().required('Full name is required'),
      mobileNumber: yup
        .string()
        .required('Mobile number is required')
        .matches(/^[6789]\d{9}$/, {
          message: 'Invalid mobile number',
        }),
      email: yup.string().required('Email is required').email(),
    },
    [
      ['surnameOne', 'DesignationOne'],
      ['surnameOne', 'teamImageOne'],
      ['teamImageOne', 'DesignationOne'],
      ['surnameTwo', 'DesignationTwo'],
      ['surnameTwo', 'teamImageTwo'],
      ['teamImageTwo', 'DesignationTwo'],
      ['surnameThree', 'DesignationThree'],
      ['surnameThree', 'teamImageThree'],
      ['teamImageThree', 'DesignationThree'],
      ['surnameFour', 'DesignationFour'],
      ['surnameFour', 'teamImageFour'],
      ['teamImageFour', 'DesignationFour'],
    ],
  );

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm<OnePagerFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      company_description: '',
      surnameOne: '',
      surnameTwo: '',
      surnameThree: '',
      surnameFour: '',
      DesignationOne: '',
      DesignationTwo: '',
      DesignationThree: '',
      DesignationFour: '',
    },
    mode: 'onTouched',
  });

  const fieldsWatch = useWatch({ control });

  const handleAllFileInputs = (e: any, type: string, name: string) => {
    if (e.target.files[0]) {
      if (
        type === 'image' &&
        (e.target.files[0].type === 'image/jpeg' ||
          e.target.files[0].type === 'image/png')
      ) {
        if (
          e.target.files &&
          e.target.files[0] &&
          e.target.files[0].size <= 3145728
        ) {
          setAllFilesImages({
            ...allFilesImages,
            [e.target.name]: e.target.files[0] && e.target.files[0],
          });
        } else {
          toast.error(`${name} image size should be less than 3mb`);
        }
      } else {
        type === 'image'
          ? toast.error('Only image can be uploaded')
          : toast.error('Only PDF file can be uploaded');
      }
    }
  };
  const previewModelClose = () => setOpenPreviewModal(false);
  const previewModelOpen = () => setOpenPreviewModal(true);

  const openEmailModal = () => setOpenEnterEmailModal(true);
  const closeEmailModel = () => {
    setLoader(false);
    setOpenEnterEmailModal(false);
    setOpenModalOfOnePager(true);
  };

  const onSubmitPreview = (data: OnePagerFormFields) => {
    setOnePagerData({
      ...data,
      ...allFilesImages,
    });
    previewModelOpen();
  };

  const onSubmitDownload = (data: OnePagerFormFields) => {
    setOnePagerData({
      ...data,
      ...allFilesImages,
    });
    openEmailModal();
  };

  const ErrorMessage = ({ text }: { text: string }) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.75rem',
        lineHeight: '1.66',
        textAlign: 'left',
        color: '#d32f2f',
      }}
    >
      <ErrorOutlineIcon color="error" sx={{ fontSize: '0.75rem', mr: 0.4 }} />
      <span>{text}</span>
    </span>
  );

  const emailSubmitHandler = async (data: { email: string } | undefined) => {
    setSubmitEmail(data || submitEmail);
    setLoader(true);
    setDownloadPdfLoading(true);
    let urlObj = imageUrls || {};
    let newObject = {};

    const imageUploadStatus =
      (imageData ? Object.keys(imageData).length : 0) ===
      Object.keys(allFilesImages).length;

    if (imageUploadStatus) {
      const allValuesOfObject = Object.values(allFilesImages);
      Object.keys(imageData).forEach((item) => {
        if (allValuesOfObject.indexOf(imageData[item]) === -1) {
          newObject[item] = allFilesImages[item];
        }
      });

      if (newObject) {
        for (const item of Object.keys(newObject)) {
          const url = await uploadFile(newObject[item], 'one-pager/');
          newObject[item] = url;
        }
      }
    }

    if (!imageUploadStatus) {
      for (const item of Object.keys(allFilesImages)) {
        const url = await uploadFile(allFilesImages[item], 'one-pager/');
        urlObj[item] = url;
      }
    }

    let obj = {
      ...onePagerData,
      ...urlObj,
      ...newObject,
      currencyState: currency === 'inr',
    };
    setTimeout(() => {
      if (data) {
        ApiPost(`generate/one-pager?email=${data.email}`, obj)
          .then((res: any) => {
            if (res.data.statusCode === 200) {
              setLoader(false);
              setDownloadPdfLoading(false);
              setOpenModalOfOnePager(false);
              setImageData(allFilesImages);
              setImageUrls(urlObj);
            } else {
            }
          })
          .catch((err) => {
            setLoader(false);
            console.log('err', err);
          });
      }
      if (!data) {
        ApiPost(`generate/one-pager`, obj, { responseType: 'blob' })
          .then((res: any) => {
            setLoader(false);
            setDownloadPdfLoading(false);
            const type = res.headers['content-type'];
            const blob = new Blob([res.data], {
              type: type,
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'Onepager.pdf';
            link.click();
          })
          .catch((err) => {
            setLoader(false);
            console.log('err', err);
          });
      }
    }, 2000);
  };

  return (
    <>
      <OnePagerPreviewModal
        openPreviewModal={openPreviewModal}
        openPreviewModalClose={previewModelClose}
        openEmailModal={openEmailModal}
        data={onePagerData}
        currencyState={currency === 'inr'}
      />
      <OnePagerCommonModal
        openEnterEmailModal={openEnterEmailModal}
        emailSubmitHandler={emailSubmitHandler}
        openEnterEmailModalClose={closeEmailModel}
        loader={loader}
        downloadPdfLoading={downloadPdfLoading}
        submitEmail={submitEmail}
        openModalOfOnePager={openModalOfOnePager}
      />
      <Box component="form">
        <Box border="1px solid #E6E1E1">
          <Box display="flex" justifyContent="flex-end" mt="28px" mx="24px">
            <CurrencyButton
              selected={currency === 'inr'}
              onClick={() => {
                setCurrency('inr');
              }}
            >
              ₹
            </CurrencyButton>
            <CurrencyButton
              selected={currency === 'usd'}
              onClick={() => {
                setCurrency('usd');
              }}
            >
              $
            </CurrencyButton>
          </Box>
          <Box p="15px">
            <Grid container>
              <Grid item>
                <label htmlFor="companyLogo">
                  <Box
                    width={120}
                    height={120}
                    border="1px solid #F4F4F4"
                    borderRadius="5px"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    textAlign="center"
                    justifyContent="space-around"
                    py={2}
                    // backgroundColor="#FAFAFA"
                  >
                    {allFilesImages.companyLogo ? (
                      <img
                        src={URL.createObjectURL(allFilesImages.companyLogo)}
                        alt="company logo"
                        style={{
                          objectFit: 'contain',
                          width: '120px',
                          height: '120px',
                        }}
                      />
                    ) : (
                      <>
                        <PhotoCameraIcon
                          sx={{ color: 'rgba(17, 17, 17, 0.3)' }}
                        />
                        <Typography
                          color="rgba(17, 17, 17, 0.3)"
                          component="span"
                          fontSize={16}
                        >
                          Upload Company Logo
                        </Typography>
                      </>
                    )}
                  </Box>
                  {errors.companyLogo ? (
                    <ErrorMessage text={errors?.companyLogo?.message || ''} />
                  ) : (
                    ''
                  )}
                </label>
                <input
                  {...register('companyLogo', {
                    onChange: (e) => {
                      handleAllFileInputs(e, 'image', 'Profile');
                    },
                  })}
                  name="companyLogo"
                  id="companyLogo"
                  type="file"
                  style={{ display: 'none' }}
                />
              </Grid>
              <Grid item sm={12} md={10} px={2}>
                <Box position="relative">
                  <CustomTextField
                    control={control}
                    variant="standard"
                    name="company_description"
                    placeholder="Enter company description"
                    fullWidth
                    inputProps={{ maxLength: 120 }}
                    // fullWidth
                    // label="Company Description"
                  />
                  <MaxLengthText
                    num={120 - (fieldsWatch.company_description?.length || 0)}
                  />
                </Box>
                <Typography
                  component="p"
                  fontSize={14}
                  color="rgba(17, 17, 17, 0.3)"
                  fontStyle="italic"
                  lineHeight="20px"
                >
                  e.g. India&apos;s first pre-selling platform committed to
                  embracing, showcasing, nurturing ideas, innovations and
                  artforms.
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid
                item
                md={4}
                mt={3}
                xs={12}
                position="relative"
                alignItems="flex-start"
              >
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Problem
                </Typography>
                <CustomTextField
                  control={control}
                  name="problem"
                  placeholder="Enter the problem statement your startup is addressing"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 120 }}
                />
                <MaxLengthText
                  num={120 - (fieldsWatch.problem?.length || 0)}
                  bottom={18}
                />
              </Grid>
              <Grid item md={4} mt={3} xs={12} position="relative">
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Solution
                </Typography>
                <CustomTextField
                  control={control}
                  name="solution"
                  placeholder="Enter the solution your startup is offering"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 120 }}
                />
                <MaxLengthText
                  num={120 - (fieldsWatch.solution?.length || 0)}
                  bottom={18}
                />
              </Grid>
              <Grid item md={4} mt={3} xs={12}>
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  {`Market (in ${currency === 'inr' ? 'Crores' : 'Million $'})`}
                </Typography>
                <MarketBox
                  control={control}
                  currency={currency}
                  name="tam"
                  fieldName="totalMarket"
                  description="Total Addressable Market"
                />
                <MarketBox
                  control={control}
                  currency={currency}
                  name="sam"
                  fieldName="serviceMarket"
                  description="Service Addressable Market"
                />
                <MarketBox
                  control={control}
                  currency={currency}
                  name="som"
                  fieldName="serviceObtMarket"
                  description="Service Obtainable Market"
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item md={6} mt={3} xs={12} position="relative">
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Competitive Landscape
                </Typography>
                <CustomTextField
                  control={control}
                  name="landscape"
                  placeholder="Mention your competitors"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 200 }}
                />
                <MaxLengthText
                  num={120 - (fieldsWatch.landscape?.length || 0)}
                />
              </Grid>
              <Grid item md={6} mt={3} xs={12} position="relative">
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Revenue Model
                </Typography>
                <CustomTextField
                  control={control}
                  name="revenue"
                  placeholder="Your startup's revenue model"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 200 }}
                />
                <MaxLengthText num={120 - (fieldsWatch.revenue?.length || 0)} />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item md={4} mt={3} xs={12} position="relative">
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Growth Strategy
                </Typography>
                <CustomTextField
                  control={control}
                  name="strategy"
                  placeholder="Few pointers on things you will do for your startup's growth"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 150 }}
                />
                <MaxLengthText
                  num={150 - (fieldsWatch.strategy?.length || 0)}
                />
              </Grid>
              <Grid item md={8} mt={3} xs={12} position="relative">
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Market Traction
                </Typography>
                <CustomTextField
                  control={control}
                  name="traction"
                  placeholder="Current market traction"
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 200 }}
                />
                <MaxLengthText
                  num={200 - (fieldsWatch.traction?.length || 0)}
                />
              </Grid>
            </Grid>
            {/*
             ***********************projections**********************
             */}
            <Typography color="#111111" fontSize={16} mb="8px" fontWeight={500}>
              Projections
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <ProjectionsTable control={control} currency={currency} />
            </Box>
            <Grid container mt={5}>
              <Grid item md={6} xs={12}>
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Team
                </Typography>
                <Grid container>
                  <TeamMember
                    imageUrl={allFilesImages.teamImageOne}
                    inputId="teamImageOne"
                    inputName="teamImageOne"
                    control={control}
                    name="surnameOne"
                    designation="DesignationOne"
                    handleAllFileInputs={handleAllFileInputs}
                    ErrorMessage={ErrorMessage}
                    errors={errors}
                    register={register}
                  />
                  <TeamMember
                    imageUrl={allFilesImages.teamImageTwo}
                    inputId="teamImageTwo"
                    inputName="teamImageTwo"
                    control={control}
                    name="surnameTwo"
                    designation="DesignationTwo"
                    handleAllFileInputs={handleAllFileInputs}
                    ErrorMessage={ErrorMessage}
                    errors={errors}
                    register={register}
                  />
                  <TeamMember
                    imageUrl={allFilesImages.teamImageThree}
                    inputId="teamImageThree"
                    inputName="teamImageThree"
                    control={control}
                    name="surnameThree"
                    designation="DesignationThree"
                    handleAllFileInputs={handleAllFileInputs}
                    ErrorMessage={ErrorMessage}
                    errors={errors}
                    register={register}
                  />
                  <TeamMember
                    imageUrl={allFilesImages.teamImageFour}
                    inputId="teamImageFour"
                    inputName="teamImageFour"
                    control={control}
                    name="surnameFour"
                    designation="DesignationFour"
                    handleAllFileInputs={handleAllFileInputs}
                    ErrorMessage={ErrorMessage}
                    errors={errors}
                    register={register}
                  />
                </Grid>
              </Grid>
              <Grid item md={6} xs={12}>
                <Typography
                  color="#111111"
                  fontSize={16}
                  mb="8px"
                  fontWeight={500}
                >
                  Funding Ask {currency === 'inr' ? '(in lakhs)' : '(in $)'}
                </Typography>
                <CustomTextFieldAmount
                  name="fundAmount"
                  control={control}
                  currency={currency}
                  placeholder="Enter Funding Amount"
                  variant="standard"
                  sx={{
                    '& input': {
                      fontSize: '24px',
                      fontWeight: 'bold',
                    },
                  }}
                />
                <Grid item>
                  <label htmlFor="fundAskingImg">
                    <Box
                      border="1px solid #F4F4F4"
                      borderRadius="5px"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      textAlign="center"
                      justifyContent="space-around"
                      py={10}
                      height={250}
                      width={250}
                    >
                      {allFilesImages.fundAskingImg ? (
                        <img
                          src={URL.createObjectURL(
                            allFilesImages.fundAskingImg,
                          )}
                          alt="Funding utilization image"
                          style={{
                            objectFit: 'contain',
                            width: '120px',
                            height: '120px',
                          }}
                        />
                      ) : (
                        <>
                          <PhotoCameraIcon
                            sx={{ color: 'rgba(17, 17, 17, 0.3)' }}
                          />
                          <Typography
                            color="rgba(17, 17, 17, 0.3)"
                            component="span"
                            fontSize={16}
                          >
                            Upload image of a piechart showing how you will
                            utilize the funding amount
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                  <input
                    {...register('fundAskingImg', {
                      onChange: (e) => {
                        handleAllFileInputs(e, 'image', 'Funding ask');
                      },
                    })}
                    name="fundAskingImg"
                    id="fundAskingImg"
                    type="file"
                    style={{ display: 'none' }}
                  />
                  {errors.fundAskingImg ? (
                    <ErrorMessage text={errors.fundAskingImg.message || ''} />
                  ) : (
                    ''
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="space-between" px={3}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Typography
                  component="span"
                  color="rgba(17,17,17,0.3)"
                  fontSize={12}
                  display="block"
                >
                  powered by
                </Typography>
                <img src={siteLogo.src} alt="pod logo" width={41} />
              </Box>
              <Box
                display="flex"
                sx={{
                  flexDirection: {
                    xs: 'row',
                  },
                }}
                alignItems="baseline"
              >
                <Typography component="span">Contact Details</Typography>
                <CustomTextField
                  name="fullName"
                  variant="standard"
                  label="Full Name"
                  control={control}
                  sx={{ ml: 2 }}
                />
                <CustomTextField
                  name="mobileNumber"
                  variant="standard"
                  label="Mobile Number"
                  control={control}
                  sx={{ ml: 2 }}
                />
                <CustomTextField
                  name="email"
                  variant="standard"
                  label="Email Id"
                  control={control}
                  sx={{ ml: 2 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" my={4}>
          <CustomButton
            sx={{ fontSize: 20, width: 248, height: 62, mr: 2 }}
            variant="outlined"
            disableElevation
            onClick={handleSubmit(onSubmitPreview)}
            type="submit"
          >
            Preview
          </CustomButton>
          <CustomButton
            sx={{ fontSize: 20, width: 300, height: 62, ml: 2 }}
            variant="contained"
            disableElevation
            onClick={handleSubmit(onSubmitDownload)}
          >
            Download for free{' '}
            <Box
              component="span"
              sx={{ ml: 1, textDecoration: 'line-through' }}
            >
              Rs.99
            </Box>
          </CustomButton>
        </Box>
      </Box>
    </>
  );
};
